<script setup>
import { computed } from 'vue';

const props = defineProps({
  installments: { type: Array, default: () => [] },
  dealValue: { type: Number, default: 0 },
});
const emit = defineEmits(['update']);

const formatBRL = v =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    v || 0
  );

const sum = computed(() =>
  props.installments.reduce((acc, p) => acc + (parseFloat(p.value) || 0), 0)
);
const sumDiverges = computed(
  () => props.dealValue > 0 && Math.abs(sum.value - props.dealValue) > 0.01
);
const paidCount = computed(() => props.installments.filter(p => p.paid).length);

const patch = (index, changes) => {
  const next = props.installments.map((p, i) =>
    i === index ? { ...p, ...changes } : p
  );
  emit('update', next);
};
const togglePaid = index =>
  patch(index, { paid: !props.installments[index].paid });
const updateValue = (index, event) =>
  patch(index, { value: parseFloat(event.target.value) || 0 });
const updateDueDate = (index, event) =>
  patch(index, { dueDate: event.target.value });

const isOverdue = p =>
  !p.paid && p.dueDate && new Date(`${p.dueDate}T23:59:59`) < new Date();
</script>

<template>
  <div class="mt-3 p-3 bg-n-surface-1 border border-n-weak rounded-lg">
    <div class="flex items-center justify-between mb-2">
      <span
        class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
      >
        Parcelas ({{ paidCount }}/{{ installments.length }} pagas)
      </span>
      <span
        class="text-xs"
        :class="
          sumDiverges ? 'text-amber-500 font-semibold' : 'text-n-slate-10'
        "
      >
        Soma: {{ formatBRL(sum) }}
      </span>
    </div>
    <p v-if="sumDiverges" class="text-xs text-amber-500 m-0 mb-2">
      ⚠️ A soma das parcelas difere do valor do negócio ({{
        formatBRL(dealValue)
      }})
    </p>
    <div class="flex flex-col gap-2">
      <div
        v-for="(p, index) in installments"
        :key="p.n"
        class="flex flex-col gap-1.5 p-2 rounded-md border border-n-weak bg-n-background"
        :class="{ 'opacity-60': p.paid }"
      >
        <!-- Linha 1: check + nº da parcela + valor -->
        <div class="flex items-center gap-2">
          <button
            v-tooltip.top="p.paid ? 'Desmarcar paga' : 'Marcar como paga'"
            class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors"
            :class="
              p.paid
                ? 'bg-emerald-500 border-emerald-500'
                : isOverdue(p)
                  ? 'border-n-ruby-9 bg-n-surface-1'
                  : 'border-n-slate-9 bg-n-surface-1 hover:border-emerald-500'
            "
            @click="togglePaid(index)"
          >
            <span v-if="p.paid" class="text-white text-xs font-bold">✓</span>
          </button>
          <span
            class="text-xs font-semibold text-n-slate-11 w-10 flex-shrink-0"
          >
            {{ p.n }}/{{ installments.length }}
          </span>
          <div class="flex items-center gap-1 flex-1 min-w-0">
            <span class="text-xs font-bold text-n-slate-11">R$</span>
            <input
              :value="p.value"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-2 py-1 rounded text-sm font-semibold bg-n-surface-1 border border-n-weak text-n-slate-12"
              :class="{ 'line-through': p.paid }"
              @change="updateValue(index, $event)"
            />
          </div>
        </div>
        <!-- Linha 2: vencimento -->
        <div class="flex items-center gap-2 pl-7">
          <span class="text-xs text-n-slate-10 flex-shrink-0">Vencimento</span>
          <input
            :value="p.dueDate"
            type="date"
            class="flex-1 px-2 py-1 rounded text-xs bg-n-surface-1 border border-n-weak"
            :class="
              isOverdue(p)
                ? 'text-n-ruby-11 border-n-ruby-6'
                : 'text-n-slate-12'
            "
            @change="updateDueDate(index, $event)"
          />
          <span
            v-if="isOverdue(p)"
            class="text-xs font-semibold text-n-ruby-11 flex-shrink-0"
          >
            Atrasada
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
